import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, User2, Briefcase, Wallet, ClipboardCheck, GraduationCap } from "lucide-react";
import { employeeApi } from "../services/employeeApi";
import { recruitmentApi } from "../services/recruitmentApi";
import { payrollApi } from "../services/payrollApi";
import { onboardingApi } from "../services/onboardingApi";
import { trainingApi } from "../services/trainingApi";

const MAX_RESULTS_PER_GROUP = 5;

export const QuickSearch = () => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [applicants, setApplicants] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [onboardingCases, setOnboardingCases] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const containerRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    const onKeyDown = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  const ensureLoaded = () => {
    if (loaded) return;
    setLoaded(true);
    employeeApi.list().then((r) => setEmployees(r.data)).catch(() => {});
    recruitmentApi.listApplicants().then((r) => setApplicants(r.data)).catch(() => {});
    payrollApi.listPeriods().then((r) => setPeriods(r.data)).catch(() => {});
    onboardingApi.listCases().then((r) => setOnboardingCases(r.data)).catch(() => {});
    trainingApi.listCourses().then((r) => setCourses(r.data)).catch(() => {});
  };

  const q = query.trim().toLowerCase();
  const showResults = open && q.length >= 2;

  const employeeMatches = showResults
    ? employees.filter((e) => `${e.legal_name} ${e.employee_id} ${e.position || ""}`.toLowerCase().includes(q)).slice(0, MAX_RESULTS_PER_GROUP)
    : [];
  const applicantMatches = showResults
    ? applicants.filter((a) => `${a.legal_name} ${a.email || ""}`.toLowerCase().includes(q)).slice(0, MAX_RESULTS_PER_GROUP)
    : [];
  const periodMatches = showResults
    ? periods.filter((p) => `${p.payroll_month}`.toLowerCase().includes(q)).slice(0, MAX_RESULTS_PER_GROUP)
    : [];
  const employeeById = {};
  employees.forEach((e) => { employeeById[e.id] = e; });
  const onboardingMatches = showResults
    ? onboardingCases
        .map((c) => ({ ...c, _employee: employeeById[c.employee_id] }))
        .filter((c) => `${c._employee?.legal_name || ""} ${c._employee?.employee_id || ""}`.toLowerCase().includes(q))
        .slice(0, MAX_RESULTS_PER_GROUP)
    : [];
  const courseMatches = showResults
    ? courses.filter((c) => `${c.title_en} ${c.title_ja || ""} ${c.key || ""}`.toLowerCase().includes(q)).slice(0, MAX_RESULTS_PER_GROUP)
    : [];
  const totalMatches = employeeMatches.length + applicantMatches.length + periodMatches.length + onboardingMatches.length + courseMatches.length;

  const go = (path) => {
    navigate(path);
    setOpen(false);
    setQuery("");
  };

  return (
    <div className="relative" ref={containerRef} data-testid="quick-search-container">
      <div className="relative hidden sm:block">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={15} />
        <input
          data-testid="quick-search-input"
          value={query}
          onFocus={ensureLoaded}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          placeholder="Search employees, applicants, onboarding, training..."
          className="w-56 lg:w-72 pl-9 pr-8 py-2 rounded-full border border-slate-200 bg-slate-50 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
        />
        {query && (
          <button
            type="button"
            data-testid="quick-search-clear-button"
            aria-label="Clear search"
            onClick={() => { setQuery(""); setOpen(false); }}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-600"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {showResults && (
        <div
          data-testid="quick-search-results"
          className="absolute mt-2 w-80 sm:w-96 right-0 sm:right-auto sm:left-0 bg-white rounded-2xl border border-slate-200 shadow-xl overflow-hidden z-50 max-h-[70vh] overflow-y-auto"
        >
          {totalMatches === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-slate-500" data-testid="quick-search-empty">No matches for "{query}"</p>
          ) : (
            <>
              {employeeMatches.length > 0 && (
                <div>
                  <p className="px-4 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-500">Employees</p>
                  {employeeMatches.map((e) => (
                    <button
                      key={e.id}
                      data-testid={`quick-search-employee-${e.employee_id}`}
                      onClick={() => go(`/management/employees/${e.id}`)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 text-left transition-colors"
                    >
                      <User2 size={15} className="text-blue-500 shrink-0" />
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-slate-800 truncate">{e.legal_name}</span>
                        <span className="block text-xs text-slate-500 truncate">{e.employee_id} · {e.position}</span>
                      </span>
                    </button>
                  ))}
                </div>
              )}
              {applicantMatches.length > 0 && (
                <div className="border-t border-slate-100">
                  <p className="px-4 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-500">Applicants</p>
                  {applicantMatches.map((a) => (
                    <button
                      key={a.id}
                      data-testid={`quick-search-applicant-${a.id}`}
                      onClick={() => go(`/management/recruitment/applicants/${a.id}`)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 text-left transition-colors"
                    >
                      <Briefcase size={15} className="text-emerald-500 shrink-0" />
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-slate-800 truncate">{a.legal_name}</span>
                        <span className="block text-xs text-slate-500 truncate">{a.email}</span>
                      </span>
                    </button>
                  ))}
                </div>
              )}
              {periodMatches.length > 0 && (
                <div className="border-t border-slate-100">
                  <p className="px-4 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-500">Payroll Periods</p>
                  {periodMatches.map((p) => (
                    <button
                      key={p.id}
                      data-testid={`quick-search-period-${p.id}`}
                      onClick={() => go(`/management/payroll/periods/${p.id}`)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 text-left transition-colors"
                    >
                      <Wallet size={15} className="text-amber-500 shrink-0" />
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-slate-800 truncate">{p.payroll_month}</span>
                        <span className="block text-xs text-slate-500 truncate">{p.status}</span>
                      </span>
                    </button>
                  ))}
                </div>
              )}
              {onboardingMatches.length > 0 && (
                <div className="border-t border-slate-100">
                  <p className="px-4 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-500">Onboarding Cases</p>
                  {onboardingMatches.map((c) => (
                    <button
                      key={c.id}
                      data-testid={`quick-search-onboarding-${c.id}`}
                      onClick={() => go(`/management/onboarding/${c.id}`)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 text-left transition-colors"
                    >
                      <ClipboardCheck size={15} className="text-teal-500 shrink-0" />
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-slate-800 truncate">{c._employee?.legal_name || "Unknown"}</span>
                        <span className="block text-xs text-slate-500 truncate">{c._employee?.employee_id} · {c.status}</span>
                      </span>
                    </button>
                  ))}
                </div>
              )}
              {courseMatches.length > 0 && (
                <div className="border-t border-slate-100">
                  <p className="px-4 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-500">Training Courses</p>
                  {courseMatches.map((c) => (
                    <button
                      key={c.id}
                      data-testid={`quick-search-course-${c.id}`}
                      onClick={() => go(`/management/training/courses/${c.id}`)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 text-left transition-colors"
                    >
                      <GraduationCap size={15} className="text-violet-500 shrink-0" />
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-slate-800 truncate">{c.title_en}</span>
                        <span className="block text-xs text-slate-500 truncate">{c.status}</span>
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};
